// @flow
import {
  Button,
  ComboBoxInput,
  Text,
  TextareaInput,
  TextInput,
  uniquenessOfEmail,
} from 'bench-internal-theme';
import React, { type Node } from 'react';
import styled from 'styled-components';

import type { Person } from './types';

const defaultClientRoleId = '2';

type WrapperElementProps = {
  children: Node,
};

const FormTitleContainer = styled.div`
  padding: 4px 12px;
`;

const FormTitle = ({ children }: WrapperElementProps) => (
  <FormTitleContainer>
    <Text styleType="title">{children}</Text>
  </FormTitleContainer>
);

const FieldSet = styled.div`
  padding: 20px 0 0;
`;

const LegendContainer = styled.div`
  padding: 0 12px 12px;
`;

const Legend = ({ children }: WrapperElementProps) => (
  <LegendContainer>
    <Text styleType="large">{children}</Text>
  </LegendContainer>
);

const FormGroup = styled.div`
  display: inline-block;
  padding: 0 12px 8px;
  width: ${({ groupWidth }) => groupWidth || '100%'};

  ${Text} {
    padding: 4px 0 8px;
  }
`;

const Label = ({ children }: WrapperElementProps) => (
  <Text styleType="small" uppercase>
    {children}
  </Text>
);

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0 12px 4px;

  ${Button} {
    padding-left: 64px;
    padding-right: 64px;
  }
`;

type CreateOrEditPersonFormProps = {
  clientRoleOptions: Array<{
    label: string,
    value: string,
  }>,
  person?: Person,
};

const CreateOrEditPersonForm = ({
  clientRoleOptions,
  person,
  ...props
}: CreateOrEditPersonFormProps) => (
  <form {...props}>
    <FormTitle>{person ? 'Edit User' : 'Create New User'}</FormTitle>
    <FieldSet>
      <Legend>Personal Info</Legend>
      <FormGroup groupWidth="50%">
        <Label>First Name</Label>
        <TextInput
          defaultValue={person && person.firstName}
          name="firstName"
          required
          type="text"
        />
      </FormGroup>
      <FormGroup groupWidth="50%">
        <Label>Last Name</Label>
        <TextInput defaultValue={person && person.lastName} name="lastName" required type="text" />
      </FormGroup>
      <FormGroup groupWidth="50%">
        <Label>Email</Label>
        <TextInput
          defaultValue={person && person.email}
          name="email"
          validates={uniquenessOfEmail()}
          required
          type="email"
        />
      </FormGroup>
      <FormGroup groupWidth="50%">
        <Label>Phone</Label>
        <TextInput defaultValue={person && person.phone} name="phone" required type="tel" />
      </FormGroup>
      <FormGroup>
        <Label>Company Name</Label>
        <TextInput defaultValue={person && person.company} name="company" required type="text" />
      </FormGroup>
    </FieldSet>
    <FieldSet>
      <Legend>User Type (Default Client Role)</Legend>
      <FormGroup groupWidth="50%">
        <ComboBoxInput
          defaultValue={
            (person &&
              clientRoleOptions.find(option => option.value === person.defaultClientRoleId)) ||
            clientRoleOptions.find(option => option.value === defaultClientRoleId)
          }
          name="defaultClientRoleId"
          options={clientRoleOptions}
        />
      </FormGroup>
    </FieldSet>
    <FieldSet>
      <Legend>Notes</Legend>
      <FormGroup>
        <TextareaInput defaultValue={person && person.notes} name="notes" rows="8" />
      </FormGroup>
    </FieldSet>
    <FormActions>
      <Button size="large" styleType="primary" type="submit">
        Save
      </Button>
    </FormActions>
  </form>
);

CreateOrEditPersonForm.defaultProps = {
  person: null,
};

export default CreateOrEditPersonForm;
